import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BusinessIdea, MonetizationStrategy, BusinessStage } from './onboarding/onboarding.types';
import { useAuth } from '@/lib';
import { saveBusinessIdea, updateBusinessIdea, updateTaskProgress } from '@/lib/business-ideas';
import { markOnboardingCompleted } from '@/lib';
import { getApp } from 'firebase/app';

// Type-safe mapping objects for select options
const MONETIZATION_OPTIONS: { value: MonetizationStrategy; label: string; tooltip: string }[] = [
  {
    value: 'subscription',
    label: 'Subscription (SaaS)',
    tooltip: 'Customers pay a recurring fee (monthly/yearly) to access your service',
  },
  {
    value: 'one-time payment',
    label: 'One-time Payment',
    tooltip: 'Customers pay once to purchase your product or service',
  },
  {
    value: 'transaction fees',
    label: 'Transaction Fees',
    tooltip: 'You take a percentage or fixed fee from each transaction between users',
  },
  {
    value: 'ads or sponsorships',
    label: 'Ads or Sponsorships',
    tooltip: 'Revenue from displaying advertisements or sponsored content',
  },
  {
    value: 'affiliate commissions',
    label: 'Affiliate Commissions',
    tooltip: 'Earn money by referring customers to other products or services',
  },
  {
    value: 'donations or tipping',
    label: 'Donations or Tipping',
    tooltip: 'Voluntary payments from users who want to support your work',
  },
  {
    value: 'licensing',
    label: 'Licensing',
    tooltip: 'Sell the rights to use your intellectual property or technology to others',
  },
  {
    value: 'usage-based',
    label: 'Usage-based',
    tooltip: 'Customers pay based on how much they use your service (pay-as-you-go)',
  },
  {
    value: 'freemium + upsell',
    label: 'Freemium + Upsell',
    tooltip: 'Offer a free basic version and charge for premium features',
  },
];

const STAGE_OPTIONS: { value: BusinessStage; label: string; tooltip: string }[] = [
  {
    value: 'idea',
    label: 'Just an idea',
    tooltip: "You have a concept but haven't started any development or research yet",
  },
  {
    value: 'validation',
    label: 'Market validation',
    tooltip:
      "You're testing if people actually want this solution through surveys, interviews, or prototypes",
  },
  {
    value: 'mvp',
    label: 'MVP in development',
    tooltip: 'Building a minimal version of your product to test with real users',
  },
  {
    value: 'launched',
    label: 'Launched (pre-revenue)',
    tooltip:
      'Your product is live and available to users, but not yet generating significant revenue',
  },
  {
    value: 'scaling',
    label: 'Scaling',
    tooltip: "Your product is successful and you're growing your user base and revenue",
  },
];

interface BusinessIdeaViewerProps {
  businessIdea: BusinessIdea;
  readOnly?: boolean;
  onSave?: (businessIdea: BusinessIdea) => void;
  onCancel?: () => void;
  showActions?: boolean;
  className?: string;
  isOnboarding?: boolean;
}

const BusinessIdeaViewer: React.FC<BusinessIdeaViewerProps> = ({
  businessIdea,
  readOnly = false,
  onSave,
  onCancel,
  showActions = true,
  className = '',
  isOnboarding = false,
}) => {
  const { user, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<BusinessIdea>(businessIdea);
  const [editingField, setEditingField] = useState<keyof BusinessIdea | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Update form data when businessIdea prop changes
  useEffect(() => {
    setFormData(businessIdea);
  }, [businessIdea]);

  const handleInputChange = (field: keyof BusinessIdea, value: string | string[]) => {
    if (readOnly) return;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    // Clear any previous errors
    setSaveError(null);

    if (isOnboarding) {
      // For onboarding, save to Firestore and then redirect
      if (user) {
        try {
          setSaving(true);

          // Ensure the business idea has the correct user ID
          const businessIdeaToSave = {
            ...formData,
            userId: user.id,
            ownerId: user.id,
            collaboratorIds: [user.id],
            updatedAt: new Date().toISOString(),
          };

          const savedBusinessIdeaId = await saveBusinessIdea(businessIdeaToSave);

          // Mark onboarding as completed since user just saved their business idea
          try {
            await markOnboardingCompleted(user.id, getApp());
          } catch (error) {
            console.error('Error marking onboarding as completed:', error);
            // Don't fail the save if onboarding completion fails
          }

          // Mark the 'select-idea' task as completed
          try {
            await updateTaskProgress(savedBusinessIdeaId, 'select-idea', user.id, 'completed', {
              businessIdeaId: savedBusinessIdeaId,
              businessIdeaName: businessIdeaToSave.name,
              message: `Business idea "${businessIdeaToSave.name}" selected and saved`,
            });
          } catch (error) {
            console.error('Error marking task as completed:', error);
            // Don't fail the save if task completion fails
          }

          // Refresh the user profile to get the updated onboardingCompleted status
          await refreshUserProfile();

          // Navigate to home page on success
          navigate('/', { replace: true });
        } catch (error) {
          console.error('Error saving business idea to Firestore:', error);
          setSaveError(error instanceof Error ? error.message : 'Failed to save business idea');
        } finally {
          setSaving(false);
        }
      }
    } else if (onSave) {
      // For non-onboarding, save to Firestore and call the onSave callback
      if (user) {
        try {
          setSaving(true);

          // Save or update the business idea in Firestore
          if (formData.id) {
            await updateBusinessIdea(formData.id, formData);
          } else {
            // If no ID, save as new business idea
            const businessIdeaToSave = {
              ...formData,
              userId: user.id,
              ownerId: user.id,
              collaboratorIds: [user.id],
              updatedAt: new Date().toISOString(),
            };
            await saveBusinessIdea(businessIdeaToSave);
          }

          // Call the original onSave callback
          onSave(formData);
        } catch (_) {
          // Still call onSave to not break existing functionality
          onSave(formData);
        } finally {
          setSaving(false);
        }
      } else {
        // Fallback to original behavior if no user
        onSave(formData);
      }
    }
  };

  const handleEditField = (field: keyof BusinessIdea) => {
    if (readOnly) return;
    setEditingField(field);
  };

  const renderMultilineField = (
    field: keyof BusinessIdea,
    label: string,
    rows: number = 3,
    tooltip?: string,
  ) => {
    const value = formData[field];
    const isEditing = editingField === field;

    if (readOnly || !isEditing) {
      return (
        <Box>
          <Box className="flex items-center justify-between mb-2">
            <Box className="flex items-center gap-1">
              <Typography variant="subtitle1" className="text-white">
                {label}
              </Typography>
              {tooltip && (
                <Tooltip title={tooltip} arrow placement="right">
                  <IconButton size="small" className="text-secondary w-5 h-5 min-w-5 min-h-5">
                    <HelpOutline sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {!readOnly && (
              <Button
                size="small"
                onClick={() => handleEditField(field)}
                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 px-3 py-1 text-sm"
              >
                Edit
              </Button>
            )}
          </Box>
          <Typography
            variant="body2"
            className="text-gray-200 bg-gray-800/50 p-4 rounded-lg border-l-4 border-blue-500 min-h-[80px] leading-relaxed"
          >
            {value || 'Not specified'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Box className="flex items-center justify-between mb-2">
          <Box className="flex items-center gap-1">
            <Typography variant="subtitle1" className="text-white">
              {label}
            </Typography>
            {tooltip && (
              <Tooltip title={tooltip} arrow placement="right">
                <IconButton size="small" className="text-secondary w-5 h-5 min-w-5 min-h-5">
                  <HelpOutline sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Button
            size="small"
            onClick={() => setEditingField(null)}
            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 px-3 py-1 text-sm"
          >
            Done
          </Button>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={rows}
          value={value || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={`Enter ${label.toLowerCase()}`}
          variant="outlined"
          size="small"
          className="bg-gray-800"
          slotProps={{
            input: {
              className: 'text-white',
              style: { color: 'white' },
            },
          }}
        />
      </Box>
    );
  };

  const renderSelectField = (
    field: keyof BusinessIdea,
    label: string,
    options: { value: string; label: string; tooltip: string }[],
    tooltip?: string,
  ) => {
    // Handle both array and string values for backward compatibility
    const rawValue = formData[field];
    const value = Array.isArray(rawValue) ? rawValue[0] || '' : rawValue || '';
    const isEditing = editingField === field;

    if (readOnly || !isEditing) {
      const selectedOption = options.find((opt) => opt.value === value);
      return (
        <Box>
          <Box className="flex items-center justify-between mb-2">
            <Box className="flex items-center gap-1">
              <Typography variant="subtitle1" className="text-white">
                {label}
              </Typography>
              {tooltip && (
                <Tooltip title={tooltip} arrow placement="right">
                  <IconButton size="small" className="text-secondary w-5 h-5 min-w-5 min-h-5">
                    <HelpOutline sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {!readOnly && (
              <Button
                size="small"
                onClick={() => handleEditField(field)}
                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 px-3 py-1 text-sm"
              >
                Edit
              </Button>
            )}
          </Box>
          <Typography
            variant="body2"
            className="text-gray-200 bg-gray-800/50 p-4 rounded-lg border-l-4 border-blue-500 leading-relaxed"
          >
            {selectedOption?.label || 'Not specified'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Box className="flex items-center justify-between mb-2">
          <Box className="flex items-center gap-1">
            <Typography variant="subtitle1" className="text-white">
              {label}
            </Typography>
            {tooltip && (
              <Tooltip title={tooltip} arrow placement="right">
                <IconButton size="small" className="text-secondary w-5 h-5 min-w-5 min-h-5">
                  <HelpOutline sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Button
            size="small"
            onClick={() => setEditingField(null)}
            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 px-3 py-1 text-sm"
          >
            Done
          </Button>
        </Box>
        <FormControl fullWidth size="small" className="bg-gray-800">
          <Select
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="text-white"
            label=""
            aria-label={label}
          >
            {options.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                className="flex items-center justify-between"
              >
                <span>{option.label}</span>
                <Tooltip title={option.tooltip} arrow placement="right">
                  <IconButton size="small" className="text-secondary w-5 h-5 min-w-5 min-h-5 ml-3">
                    <HelpOutline sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <Box className={`${className}`}>
      <Card
        className="bg-gradient-to-br from-app-surface to-gray-800 border border-gray-600 shadow-2xl hover:shadow-blue-500/10"
        sx={{ borderRadius: '8px' }}
      >
        <CardContent className="p-8 space-y-12">
          {/* Business Name with title styling */}
          <Box className="text-center">
            {editingField === 'name' ? (
              // Editing mode - show text field
              <Box>
                <TextField
                  fullWidth
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter business name"
                  variant="outlined"
                  size="small"
                  className="bg-gray-800 mb-3"
                  slotProps={{
                    input: {
                      className: 'text-white text-center text-2xl font-bold',
                      style: { color: 'white' },
                    },
                  }}
                />
                <Box className="flex justify-center mt-2">
                  <Button
                    size="small"
                    onClick={() => setEditingField(null)}
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 px-3 py-1 text-sm"
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            ) : (
              // View mode - show styled title
              <Box className="group">
                <Typography
                  variant="h4"
                  className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-600 bg-clip-text text-transparent font-bold pb-2 cursor-pointer hover:opacity-80 transition-all duration-200 group-hover:scale-105"
                  onClick={() => !readOnly && handleEditField('name')}
                >
                  {formData.name || 'Untitled Business'}
                </Typography>
                <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mb-6 transition-all duration-200 group-hover:w-32 group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400"></div>
                {!readOnly && (
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center">
                    <span className="text-blue-400 text-sm">Click to edit</span>
                  </div>
                )}
              </Box>
            )}
          </Box>

          {/* Summary */}
          <Box>
            {renderMultilineField(
              'summary',
              'Elevator Pitch',
              2,
              'A concise summary of your business idea that you could explain to someone in the time it takes to ride an elevator together',
            )}
            {renderMultilineField('description', 'Description', 4)}
          </Box>

          {/* Problem and Solution */}
          <Box>
            <Box className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <Typography variant="h5" className="text-white font-semibold">
                Problem & Solution
              </Typography>
            </Box>

            <Box className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              {renderMultilineField('problemStatement', 'Problem Statement', 3)}
              {renderMultilineField('solutionOverview', 'Solution Overview', 3)}
            </Box>
          </Box>

          {/* Business Model and Revenue */}
          <Box>
            <Box className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>
              <Typography variant="h5" className="text-white font-semibold">
                Business Model & Revenue
              </Typography>
            </Box>

            <Box className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
              {renderSelectField(
                'monetization',
                'Revenue Model',
                MONETIZATION_OPTIONS,
                'How will you generate revenue from this business?',
              )}
              {renderSelectField(
                'stage',
                'Current Stage',
                STAGE_OPTIONS,
                'What stage is your business currently in?',
              )}
            </Box>
          </Box>

          {/* Target Market and Competitive Advantage */}
          <Box>
            <Box className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <Typography variant="h5" className="text-white font-semibold">
                Market & Competition
              </Typography>
            </Box>

            <Box className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              {renderMultilineField(
                'targetAudience',
                'Target Market',
                3,
                'Your target market is the specific group of customers most likely to buy your product or service.',
              )}
              {renderMultilineField(
                'differentiator',
                'Competitive Advantage',
                3,
                'What makes your solution unique or better than existing alternatives?',
              )}
            </Box>
          </Box>

          {/* Error Message */}
          {saveError && (
            <Box className="bg-red-900/20 border border-red-500 rounded-lg p-4 mb-4">
              <Typography variant="body2" className="text-red-300">
                <strong>Error saving business idea:</strong> {saveError}
              </Typography>
              <Typography variant="caption" className="text-red-400 block mt-1">
                Please try again or contact support if the problem persists.
              </Typography>
            </Box>
          )}

          {/* Actions */}
          {showActions && !readOnly && (onSave || onCancel || isOnboarding) && (
            <Box className="flex justify-center gap-6 pt-4">
              {onCancel && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={onCancel}
                  className="border-gray-600 text-gray-300 hover:border-gray-500 hover:bg-gray-800 px-8 py-3 transition-all duration-200 transform hover:scale-105"
                >
                  Cancel
                </Button>
              )}
              {(onSave || isOnboarding) && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-12 py-3 text-lg font-medium shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-xl"
                >
                  {saving
                    ? isOnboarding
                      ? 'Saving...'
                      : 'Saving Changes...'
                    : isOnboarding
                      ? 'Ready to Start Building'
                      : 'Save Changes'}
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BusinessIdeaViewer;
